import React, { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation, CurrencyAvatar } from '@/components'
import { ChevronDown } from '@/assets/icons'
import {
  SellMoneyAmountDisplay,
  SellMoneyConversionRate,
  SellMoneyNumberPad,
  SellMoneyButtonConfirmation,
  ResetSellMoneyOnRouteEntry,
  SellMoneyMaximumButton,
  SellMoneyCurrencyBottomSheet,
  SellMoneyEstimatedFee,
} from '@/features/sell-money'
import { useSellMoneyStore } from '@/store'
import {
  useGetWallet,
  useGetFeaturesSettings,
  type WalletCurrency,
} from '@/domain'
import { useUserStore } from '@/store/useUserStore'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'

export const SelectCurrencySellScreen: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const offsets = useOffsets()
  const theme = useTheme()

  const {
    fromCurrency,
    toCurrency,
    setFromCurrency,
    setToCurrency,
    isCurrencyModalOpen,
    setIsCurrencyModalOpen,
  } = useSellMoneyStore()

  // Get wallet data
  const { data: walletData } = useGetWallet()
  const { data: featureSettings } = useGetFeaturesSettings()
  const completeCurrencies = useUserStore((s) => s.wallet?.completeCurrencies)

  // Get currency data from location.state (passed from CurrencyDetails)
  const currencyFromState = location.state as WalletCurrency

  // Filter currencies that can be sold (exclude USDT as it's the "to" currency)
  const filteredCurrencies = useMemo(() => {
    const allCurrencies =
      walletData?.completeCurrencies || completeCurrencies || []

    // Exclude USDT from the list since it's the target currency
    const sellableCurrencies = allCurrencies.filter((c) => c.code !== 'USDT')

    // Filter by feature settings if available
    const availableSelectorCurrencies =
      featureSettings?.features.withdraw.currencies?.items

    if (!availableSelectorCurrencies?.length) {
      return sellableCurrencies
    }

    return sellableCurrencies.filter((currency) =>
      availableSelectorCurrencies.some(
        (availableCurrency: any) => availableCurrency === currency.code,
      ),
    )
  }, [
    walletData?.completeCurrencies,
    completeCurrencies,
    featureSettings?.features.withdraw.currencies?.items,
  ])

  // Set currencies on mount
  useEffect(() => {
    // Set from currency (the currency being sold)
    if (currencyFromState && !fromCurrency) {
      setFromCurrency(currencyFromState)
    }

    // Set to currency (always USDT)
    if (!toCurrency) {
      const allCurrencies =
        walletData?.completeCurrencies || completeCurrencies || []
      const usdtCurrency = allCurrencies.find((c) => c.code === 'USDT')
      if (usdtCurrency) {
        setToCurrency(usdtCurrency)
      }
    }
  }, [
    currencyFromState,
    fromCurrency,
    toCurrency,
    setFromCurrency,
    setToCurrency,
    walletData,
    completeCurrencies,
  ])

  // Redirect to dashboard if no currency data
  useEffect(() => {
    if (!currencyFromState || !currencyFromState.code) {
      navigate('/dashboard')
    }
  }, [currencyFromState, navigate])

  const handleBackPress = () => {
    navigate(-1)
  }

  const handleCurrencySelect = () => {
    setIsCurrencyModalOpen(true)
  }

  const handleCloseCurrencyModal = () => {
    setIsCurrencyModalOpen(false)
  }

  const handleCurrencyChange = (currency: WalletCurrency) => {
    setFromCurrency(currency)
    handleCloseCurrencyModal()
  }

  // Check if currencies are available
  if (!fromCurrency || !toCurrency) {
    return (
      <BaseView flex={1} center>
        <BaseText>{'Loading...'}</BaseText>
      </BaseView>
    )
  }

  return (
    <>
      <ResetSellMoneyOnRouteEntry currency={currencyFromState} />
      <SellMoneyEstimatedFee />

      <BaseView
        flex={1}
        bgColor="background.screen"
        style={{
          height: '100vh',
          paddingTop: offsets.top + 70,
        }}
      >
        {/* Sticky Header */}
        <StickyHeaderNavigation
          title={t('form:actions.sell')}
          onBackPress={handleBackPress}
        />

        <BaseView flex={1} px={12} className="flex flex-col h-full">
          {/* Central area with amount display */}
          <BaseView
            align="center"
            gap={32}
            className="flex-1 flex flex-col justify-start"
            style={{
              paddingTop: 28,
            }}
          >
            <BaseView gap={20} center>
              <SellMoneyAmountDisplay />
              <SellMoneyConversionRate />
              <SellMoneyMaximumButton />
            </BaseView>

            {/* Currency Flow Display: From → To */}
            <BaseView center mb={20}>
              <BaseView
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                {/* From Currency - Clickable */}
                <BaseView
                  onClick={handleCurrencySelect}
                  className="px-4 py-3 rounded-full cursor-pointer hover:opacity-80 transition-opacity duration-200"
                  style={{
                    backgroundColor: theme.colors.background.cardItemRoot,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <CurrencyAvatar
                    uri={fromCurrency.icon}
                    bgColor={fromCurrency.avatarBGRefColor}
                    size={24}
                    iconSize={15}
                    fallbackText={fromCurrency.code?.substring(0, 2) || '?'}
                  />
                  <BaseText
                    fontSize={16}
                    fontFamily="semibold"
                    color="label.default"
                  >
                    {fromCurrency.code}
                  </BaseText>
                  <ChevronDown size={16} color={theme.colors.label.default} />
                </BaseView>

                {/* Arrow */}
              </BaseView>
            </BaseView>
          </BaseView>

          {/* Bottom area with keyboard and button - fixed at bottom */}
          <BaseView
            className="flex-shrink-0"
            style={{
              gap: 20,
            }}
          >
            <SellMoneyNumberPad />
            <div style={{ paddingBottom: offsets.defaultBottomOffset }}>
              <SellMoneyButtonConfirmation />
            </div>
          </BaseView>
        </BaseView>

        {/* Currency Bottom Sheet */}
        <SellMoneyCurrencyBottomSheet
          isOpen={isCurrencyModalOpen}
          onClose={handleCloseCurrencyModal}
          currencies={filteredCurrencies}
          selectedCurrency={fromCurrency}
          onSelectCurrency={handleCurrencyChange}
        />
      </BaseView>
    </>
  )
}
